import type { Dispatch, AnyAction } from 'redux';

import * as FindAllForCompanyTableAPI from 'ab-api/web/employee/findAllForCompanyTable';
import * as FindByIdAPI from 'ab-api/web/employee/findById';
import * as FindDeactivatedListAPI from 'ab-api/web/employee/findDeactivatedList';
import * as FindDeactivatedAPI from 'ab-api/web/employee/findDeactivatedTable';
import * as RelatedWorkOrdersAPI from 'ab-api/web/employee/findRelatedWorkOrdersTable';
import * as SetEmployeeToUnavailableAPI from 'ab-api/web/employee/setToUnavailable';
import * as UpdateEmployeeAPI from 'ab-api/web/employee/update';

import type { EmployeeOptionVM as ExtendedOptionVM } from 'ab-viewModels/employee/extendedOption.viewModel';
import type { FieldReportAccessOptionVM } from 'ab-viewModels/employee/fieldReportAccessOption.viewModel';
import type { EmployeeListViewModel } from 'ab-viewModels/employee/list.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';
import type { EmployeeStatusHistoryTableViewModel } from 'ab-viewModels/employeeStatusHistoryTable.viewModel';
import type { LatestNotificationsByEmployeeViewModel } from 'ab-viewModels/latestNotificationsForEmployees.viewModel';
import type * as ResourceStatusesViewModel from 'ab-viewModels/resources/resourceStatuses.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import { toRequestModelWithFormattedPhoneNumbers } from 'af-utils/form.util';
import { http } from 'af-utils/http.util';

import { RESOURCE_STATUS } from 'af-constants/reduxForms';
import API from 'af-constants/routes/api';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

export function update(form: UpdateEmployeeAPI.W_Employee_Update_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			await http.put(UpdateEmployeeAPI.URL(form.id, TimeUtils.formatDate(new Date(), TimeFormat.DB_DATE_ONLY)), form);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function downEmployee(employeeId: number, form: SetEmployeeToUnavailableAPI.W_Employee_SetToUnavailable_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.post<void>(SetEmployeeToUnavailableAPI.URL(employeeId), form);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findById(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			let employee = await http.get<FindByIdAPI.W_Employee_FindById_VM>(FindByIdAPI.URL(id));
			employee = toRequestModelWithFormattedPhoneNumbers(employee, ['phoneNumber', 'countryCode']);
			return employee;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyList(filterText?: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeListViewModel>(API.V1.RESOURCES.EMPLOYEE.LIST(filterText));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllDeactivatedForCompanyList(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<FindDeactivatedListAPI.W_Employee_FindDeactivatedList_VM>(FindDeactivatedListAPI.URL(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForCompanyTable(
	tableRequestModel: TableQuery,
	additionalFilter: FindAllForCompanyTableAPI.W_Employee_FindAllForCompanyTable_QP_Filters
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<FindAllForCompanyTableAPI.W_Employee_FindAllForCompanyTable_VM>(FindAllForCompanyTableAPI.URL(data, additionalFilter));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllDeactivatedForCompanyTable(tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<FindDeactivatedAPI.W_Employee_FindDeactivated_VM>(FindDeactivatedAPI.URL(data));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSuperintendents() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.SUPERINTENDENTS());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForShipping() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.FIND_FOR_SHIPPING());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSuperintendentsForFieldReport(workOrderId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => await http.get<FieldReportAccessOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.SUPERINTENDENTS_FOR_FIELD_REPORT(workOrderId));
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllProjectManagers() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.PROJECT_MANAGERS());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSuperintendentsAndProjectManagers() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.SUPERINTENDENTS_AND_PROJECT_MANAGERS());
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllEmployeesForWorkOrders() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<EmployeeOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.WORK_ORDER_EMPLOYEES());
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllEmployeesForWorkOrdersForDueDate(dueDate: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<ExtendedOptionVM[]>(API.V1.RESOURCES.EMPLOYEE.WORK_ORDER_EMPLOYEES_BY_DUE_DATE(dueDate));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findStatusHistoryForEmployee(
	employeeId: string,
	tableRequestModel: TableQuery,
	startDate: Date,
	endDate: Date,
	filterByStatusId: number
) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
			return await http.get<TableContent<EmployeeStatusHistoryTableViewModel>>(
				API.V1.RESOURCES.EMPLOYEE.STATUS_HISTORY(employeeId, data, startDateString, endDateString, filterByStatusId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllStatusesForEmployee(employeeId: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<ResourceStatusesViewModel.Default>(
				API.V1.RESOURCES.EMPLOYEE.ALL_STATUSES_FOR_EMPLOYEE(employeeId),
				{ submitting: RESOURCE_STATUS }
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findLatestNotificationsForEmployeesOnWorkOrder(employeeIds: number[], workOrderId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<LatestNotificationsByEmployeeViewModel>(
				API.V1.RESOURCES.EMPLOYEE.FIND_LATEST_NOTIFICATIONS_FOR_WORK_ORDER(employeeIds, workOrderId)
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findRelatedWorkOrders(employeeId: number, tableRequestModel: TableQuery, startDate: Date, endDate: Date) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const tableData = new TableQuery(tableRequestModel);
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);
			return await http.get<RelatedWorkOrdersAPI.W_Employee_FindRelatedWorkOrdersTable_VM>(
				RelatedWorkOrdersAPI.URL(employeeId, startDateString, endDateString, tableData)
			);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}
